class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.bindOverlayClose();
    this.setHeaderCartIconAccessibility();
  }

  bindOverlayClose() {
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (!overlay || overlay.dataset.bound === 'true') return;
    overlay.dataset.bound = 'true';
    overlay.addEventListener('click', this.close.bind(this));
  }

  setHeaderCartIconAccessibility() {
    document.querySelectorAll('#cart-icon-bubble, [data-open-cart-drawer]').forEach((cartLink) => {
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(cartLink);
      });
      cartLink.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  markFilled() {
    this.classList.remove('is-empty');
    this.querySelector('.drawer__inner')?.classList.remove('is-empty');
    this.querySelector('cart-drawer-items')?.classList.remove('is-empty');
  }

  forceEagerCartImages() {
    this.querySelectorAll('.cart-item__image').forEach((img) => {
      img.loading = 'eager';
      img.setAttribute('loading', 'eager');
      img.setAttribute('fetchpriority', 'high');
      if (img.dataset.src && !img.getAttribute('src')) img.src = img.dataset.src;
    });
  }

  preloadCartImages() {
    const imgs = [...this.querySelectorAll('.cart-item__image')];
    this.forceEagerCartImages();
    const tasks = imgs.map((img) => {
      const src = img.currentSrc || img.src;
      if (!src) return Promise.resolve();
      if (img.complete && img.naturalWidth > 0) return Promise.resolve();
      return new Promise((resolve) => {
        const probe = new Image();
        probe.onload = probe.onerror = () => resolve();
        probe.src = src;
      });
    });
    return Promise.race([Promise.all(tasks), new Promise((resolve) => setTimeout(resolve, 400))]);
  }

  renderContents(parsedState, options = {}) {
    const shouldOpen = options.open !== false;
    if (!parsedState || !parsedState.sections) return;

    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const html = parsedState.sections[section.id];
      if (!html) return;

      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;

      const inner = this.getSectionInnerHTML(html, section.selector);
      if (inner == null) return;
      sectionElement.innerHTML = inner;
    });

    this.markFilled();
    this.forceEagerCartImages();
    this.bindOverlayClose();

    if (shouldOpen) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => this.open());
      });
    }
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const node = parsed.querySelector(selector) || parsed.querySelector('.shopify-section');
    return node ? node.innerHTML : null;
  }

  static async addAndOpen(variantId, quantity) {
    const root = window.Shopify?.routes?.root || '/';
    const qty = Number(quantity) || 1;
    const id = Number(variantId);
    if (!id) throw new Error('Missing variant');

    const sectionIds = 'cart-drawer,cart-icon-bubble';

    const addRes = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id, quantity: qty }],
        sections: sectionIds,
        sections_url: window.location.pathname,
      }),
    });
    const addData = await addRes.json();
    if (!addRes.ok) throw new Error(addData.description || addData.message || 'Error');

    if (!addData.sections || !addData.sections['cart-drawer']) {
      const sectionsRes = await fetch(`${root}cart?sections=${sectionIds}`);
      if (!sectionsRes.ok) throw new Error('No se pudo actualizar el carrito');
      addData.sections = await sectionsRes.json();
    }

    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer || typeof cartDrawer.renderContents !== 'function') {
      window.location.href = `${root}cart`;
      return addData;
    }

    cartDrawer.markFilled();
    cartDrawer.renderContents(addData, { open: false });
    await cartDrawer.preloadCartImages();
    cartDrawer.open();

    return addData;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);
window.bousprayAddToCart = CartDrawer.addAndOpen.bind(CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
